import { Button } from '@/components/atoms/Button';
import { Checkbox } from '@/components/atoms/Checkbox';
import { TextInput } from '@/components/atoms/TextInput';
import { Typography } from '@/components/atoms/Typography';
import { ChevronRightIcon, SearchIcon } from '@/components/atoms/icons';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useAppStore } from '@/core/stores/appStore';
import { usePaymentFilterStore } from '@/core/stores/paymentFilterStore';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function PaymentFilterScreen() {
    useCatalogDynatraceView(DT_SCREEN.PAYMENT_FILTER, 'payment_filter_viewed', true);
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const properties = useAppStore((state) => state.properties);

    // Get filter state from Zustand store
    const selectedPropertyIds = usePaymentFilterStore((state) => state.selectedPropertyIds);
    const setSelectedPropertyIds = usePaymentFilterStore((state) => state.setSelectedPropertyIds);
    
    // Local state for UI (checkboxes)
    const [selectedProperties, setSelectedProperties] = useState<string[]>(selectedPropertyIds);
    const [searchQuery, setSearchQuery] = useState('');

    // Sync local state with store on mount
    useEffect(() => {
        setSelectedProperties(selectedPropertyIds);
    }, [selectedPropertyIds]);

    const handleBackPress = () => {
        router.back();
    };

    const handlePropertyToggle = (propertyId: string) => {
        setSelectedProperties(prev =>
            prev.includes(propertyId)
                ? prev.filter(id => id !== propertyId)
                : [...prev, propertyId]
        );
    };

    const handleSave = () => {
        trackCatalogEvent({
            screen: DT_SCREEN.PAYMENT_FILTER,
            actionName: 'payment_filter_apply',
            eventType: 'action',
        });
        // Save selected properties to Zustand store
        setSelectedPropertyIds(selectedProperties);
        
        // Navigate back - payment screen will read from store
        router.back();
    };

    // Filter properties based on search query
    const filteredProperties = useMemo(() => {
        if (!searchQuery.trim()) {
            return properties;
        }
        const query = searchQuery.toLowerCase();
        return properties.filter(property => {
            const unitNumber = property.unitNumber?.toLowerCase() || '';
            const projectName = property.projectName?.toLowerCase() || '';
            return unitNumber.includes(query) || projectName.includes(query);
        });
    }, [properties, searchQuery]);

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2, paddingTop: insets.top }]}>
            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
                    <View
                        style={[
                            styles.backIconContainer,
                            { transform: [{ rotate: '180deg' }, { scaleX: isRTL ? -1 : 1 }] },
                        ]}
                    >
                        <ChevronRightIcon size={24} color={colors.fontPrimary} />
                    </View>
                </TouchableOpacity>
                <View style={styles.headerSpacer} />
            </View>

            <ScrollView
                contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 100 }]}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.content}>
                    {/* Title */}
                    <Typography variant="title3" color="primary" style={[styles.title, { textAlign: isRTL ? 'right' : 'left' }]}>
                        {t('payments.selectProperties')}
                    </Typography>

                    {/* Search Input */}
                    <TextInput
                        placeholder={t('payments.searchPropertiesPlaceholder')}
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        variant="search"
                        leftIcon={<SearchIcon size={24} />}
                    />

                    {/* Properties List */}
                    <View style={styles.propertiesContainer}>
                        {filteredProperties.length === 0 ? (
                            <View style={styles.emptyState}>
                                <Typography variant="body" color="tertiary" style={styles.emptyText}>
                                    {t('properties.noPropertiesFound')}
                                </Typography>
                            </View>
                        ) : (
                            filteredProperties.map((property) => {
                                // Create display name: unit number or project name
                                const displayName = property.unitNumber || property.projectName || 'Unknown Property';
                                // Create description: project name and bedrooms if available
                                const description = property.bedrooms
                                    ? `${property.projectName}${property.bedrooms ? `, ${property.bedrooms}BHK` : ''}`
                                    : property.projectName;

                                return (
                                    <View key={property.id} style={[styles.propertyCard, { backgroundColor: colors.backgroundLevel1 }]}>
                                        <TouchableOpacity
                                            style={styles.propertyContent}
                                            onPress={() => handlePropertyToggle(property.id)}
                                            activeOpacity={0.7}
                                        >
                                            <Checkbox
                                                isSelected={selectedProperties.includes(property.id)}
                                                onPress={() => handlePropertyToggle(property.id)}
                                                size={24}
                                            />
                                            <View style={styles.propertyInfo}>
                                                <Typography variant="headline" color="primary" style={[styles.propertyName, { textAlign: isRTL ? 'right' : 'left' }]}>
                                                    {displayName}
                                                </Typography>
                                                {Boolean(description) && (
                                                    <Typography variant="subhead" color="tertiary" style={[styles.propertyDescription, { textAlign: isRTL ? 'right' : 'left' }]}>
                                                        {description}
                                                    </Typography>
                                                )}
                                            </View>
                                        </TouchableOpacity>
                                    </View>
                                );
                            })
                        )}
                    </View>
                </View>
            </ScrollView>

            {/* Bottom Gradient and Save Button */}
            <View style={styles.bottomGradient} />
            <View style={[styles.bottomContainer, { paddingBottom: insets.bottom + 16 }]}>
                <Button
                    title={t('payments.save')}
                    variant="primary"
                    onPress={handleSave}
                    fullWidth={true}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 20,
        height: 70,
    },
    backButton: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
    backIconContainer: {
        // Transform (rotate + scaleX for RTL) applied inline so back arrow flips in RTL
    },
    headerSpacer: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    content: {
        paddingHorizontal: 16,
        gap: 12,
    },
    title: {
        fontSize: 20,
        fontWeight: '500',
    },
    propertiesContainer: {
        gap: 8,
    },
    propertyCard: {
        borderRadius: 12,
        padding: 16,
    },
    propertyContent: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
    },
    propertyInfo: {
        flex: 1,
        gap: 4,
    },
    propertyName: {
        fontSize: 16,
        fontWeight: '600',
    },
    propertyDescription: {
        fontSize: 15,
        fontWeight: '400',
        lineHeight: 20,
    },
    bottomGradient: {
        position: 'absolute',
        bottom: 96,
        left: 0,
        right: 0,
        height: 96,
        backgroundColor: 'transparent',
    },
    bottomContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        paddingHorizontal: 16,
        paddingTop: 16,
    },
    emptyState: {
        paddingVertical: 48,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyText: {
        textAlign: 'center',
    },
});
